'use client'
import { Button } from '@navinc/base-react-components/wayfinder'
import { ReactNode } from 'react'

import {
  MarketplaceBusinessCreditCard,
  MarketplaceLoanOffer,
  MarketplacePersonalCreditCard,
  MarketplaceProductOption,
  OfferData,
} from '@/app/_components/OffersProvider/offers-provider-context.js'
import { useOffer } from '@/app/_components/OffersProvider/use-offers.js'

import { PreviewWarningInline } from '../../_components/PreviewWarning/index.jsx'
import { isBlockKind } from '../is-block-kind.js'
import { CMSLink, CMSLinkProps } from '@/app/_components/Link/index.jsx'

type OfferMeta = {
  link?: Partial<CMSLinkProps> | null
  selectedKey: string
  marketplaceOffer: { offerId: string; category: string }
}
type OfferMetaBlockType = {
  dynamicOffer: OfferMeta
}

export const isOfferMetaBlock = (block: unknown): block is OfferMetaBlockType => {
  return isBlockKind(block, 'CmsOfferMetaInlineBlock', 'offerMeta')
}

/* eslint-disable @typescript-eslint/no-unnecessary-condition -- not sure if the fields are always present */
const businessLoanDataKeys: { [key: string]: (data: MarketplaceLoanOffer) => ReactNode } = {
  title: (data) => data.name,
  cost: (data) => data.specifics?.cost?.description ?? `${data.name} cost`,
  fundingAmount: (data) => data.specifics?.fundingAmount?.description ?? `${data.name} funding amount`,
  fundingSpeed: (data) => data.specifics?.fundingSpeed?.description ?? `${data.name} funding speed`,
  repaymentTerms: (data) => data.specifics?.repaymentTerms?.description ?? `${data.name} repayment terms`,
  originationFee: (data) => data.specifics?.originationFee?.description ?? `${data.name} origination fee`,
  application: (data) => data.specifics?.application?.description ?? `${data.name} application`,
  repaymentDuration: (data) => data.specifics?.repaymentFrequency?.description ?? `${data.name} repayment duration`,
}

const creditCardDataKeys: {
  [key: string]: (data: MarketplaceBusinessCreditCard | MarketplacePersonalCreditCard) => ReactNode
} = {
  title: (data) => data.name,
  purchaseApr: (data) => data.aprs?.purchaseApr?.description ?? `${data.name} purchase apr`,
  introPurchaseAPR: (data) => data.aprs?.introPurchaseApr?.description ?? `${data.name} intro apr`,
  annualFee: (data) => data.fees?.annualFee?.description ?? `${data.name} annual fee`,
  foreignTransactionFee: (data) =>
    data.fees?.foreignTransactionFee?.description ?? `${data.name} foreign transaction fee`,
  rewardsRate: (data) => data.rewards?.rewardsRate?.description ?? `${data.name} rewards rate`,
  welcomeOffer: (data) => data.rewards?.welcomeOffer?.description ?? `${data.name} welcome offer`,
  ratesFeesLink: (data) => (
    <Button variant="primaryPlain" asChild>
      <a href={data.terms.link} target="_blank" style={{ fontSize: 'inherit', lineHeight: 'inherit' }} rel="noreferrer">
        {data.terms.linkText}
      </a>
    </Button>
  ),
}
const productOptionDataKeys: { [key: string]: (data: MarketplaceProductOption) => ReactNode } = {
  title: (data) => data.name,
}
/* eslint-enable @typescript-eslint/no-unnecessary-condition -- not sure if the fields are always present */

const normalizeCategory = (category: string): string => {
  if (category === 'businessCreditCards' || category === 'personalCreditCards') {
    return 'creditCards'
  }
  if (category === 'businessLoans') {
    return 'businessLoans'
  }
  return 'productCategories'
}

// TODO: you don't need this to be so abstract.  Maybe just create mappings separately

const getOfferSelectedKeyData = (data: OfferData, category: string, selectedKey: string): ReactNode | null => {
  const normalizedCategory = normalizeCategory(category)
  if (normalizedCategory === 'creditCards') {
    const accessor = creditCardDataKeys[selectedKey]
    return accessor?.(data as MarketplaceBusinessCreditCard | MarketplacePersonalCreditCard) ?? null
  }
  if (normalizedCategory === 'businessLoans') {
    const accessor = businessLoanDataKeys[selectedKey]
    return accessor?.(data as MarketplaceLoanOffer) ?? null
  }
  if (normalizedCategory === 'productCategories') {
    const accessor = productOptionDataKeys[selectedKey]
    return accessor?.(data as MarketplaceProductOption) ?? null
  }
  return <PreviewWarningInline>Could not determine offer type for {category}</PreviewWarningInline>
}

export const OfferMetaBlock = ({ data }: { data: OfferMeta }) => {
  const offerFromProvider = useOffer(data.marketplaceOffer.category ?? '', data.marketplaceOffer.offerId ?? '')

  if (!offerFromProvider || !offerFromProvider.data) {
    return (
      <PreviewWarningInline>
        Unable to load offer {data.marketplaceOffer.category} {data.marketplaceOffer.offerId}
      </PreviewWarningInline>
    )
  }

  const offerSelectedKeyData = getOfferSelectedKeyData(
    offerFromProvider.data,
    data.marketplaceOffer.category,
    data.selectedKey,
  )

  const link = data.link

  if (
    link &&
    ((link.type === 'reference' && typeof link.reference?.value === 'object' && link.reference.value?.slug) ||
      (link.type === 'custom' && link.url) ||
      (link.type === 'reusable' && link.reusable?.value?.url))
  ) {
    return <CMSLink {...link}>{offerSelectedKeyData}</CMSLink>
  }
  return <span>{offerSelectedKeyData}</span>
}
